import { useQuerySpecialtyTranscript } from '@/hooks/query/useQuerySpecialtyTranscript';
import { useAtom, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDebounce, useDebouncedCallback } from 'use-debounce';

import {
  DEFAULT_EXAMPLE_CONTENT,
  DEFAULT_STRUCTURE_CONTENT,
  onboardingExampleNoteAtom,
  onboardingExampleNoteForTemplateAtom,
  onboardingExampleNoteGeneratingAtom,
} from '@heidi/growth';
import { useMutationPublicExampleNoteGeneration } from './useMutationPublicExampleNoteGeneration';

const TEMPLATE_CONTENT_DEBOUNCE_MS = 1000;

interface UseTemplateReviewStateProps {
  templateName?: string;
  templateContent?: string;
  specialtyName?: string;
  onContentChange?: (content: string) => void;
  onTemplateNameChange?: (name: string) => void;
}

export function useTemplateReviewState({
  templateName,
  templateContent,
  specialtyName,
  onContentChange,
  onTemplateNameChange,
}: UseTemplateReviewStateProps) {
  const { locale } = useIntl();
  const [activeTab, setActiveTab] = useState<'example' | 'structure'>('example');
  const [localTemplateName, setLocalTemplateName] = useState(templateName || '');
  const titleInputRef = useRef<HTMLInputElement>(null);

  // Update local name when prop changes (e.g., after initial generation)
  useEffect(() => {
    if (templateName) {
      setLocalTemplateName(templateName);
    }
  }, [templateName]);

  // Saved indicator state
  const [showSaved, setShowSaved] = useState(false);
  const savedTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { data: specialtyTranscript } = useQuerySpecialtyTranscript(
    specialtyName,
    locale
  );

  const { mutate: generateExampleNote, isPending: isGeneratingExampleNote } =
    useMutationPublicExampleNoteGeneration();

  // Example content lives in atoms so it survives navigating away from this
  // step and back (avoids regenerating when the user returns to review).
  // Edits to the example tab still don't persist — those stay local via
  // `setLocalExampleContent` below, which writes to the atom.
  const [persistedExampleNote, setPersistedExampleNote] = useAtom(
    onboardingExampleNoteAtom
  );
  const [persistedExampleForTemplate, setPersistedExampleForTemplate] = useAtom(
    onboardingExampleNoteForTemplateAtom
  );
  const setIsGeneratingExampleNoteAtom = useSetAtom(onboardingExampleNoteGeneratingAtom);
  const localExampleContent = persistedExampleNote ?? DEFAULT_EXAMPLE_CONTENT;
  const setLocalExampleContent = setPersistedExampleNote;

  // Mirror the mutation's pending state into the atom so the
  // `TemplateReviewStep` (a sibling component) can disable its Continue
  // button while generation is in flight. This prevents users from
  // navigating away mid-generation and coming back to an infinite loader.
  useEffect(() => {
    setIsGeneratingExampleNoteAtom(isGeneratingExampleNote);
  }, [isGeneratingExampleNote, setIsGeneratingExampleNoteAtom]);

  // Debounce templateContent so rapid edits don't spam the API.
  const [debouncedTemplateContent] = useDebounce(
    templateContent,
    TEMPLATE_CONTENT_DEBOUNCE_MS
  );

  // Monotonic counter to discard stale mutation responses. Incremented on
  // every generation call; onSuccess/onError check their captured value
  // against the current ref to avoid overwriting with older results.
  const generationIdRef = useRef(0);

  useEffect(() => {
    const transcript = specialtyTranscript?.transcript;
    if (!transcript || !debouncedTemplateContent) return;
    if (debouncedTemplateContent === persistedExampleForTemplate) return;

    setPersistedExampleForTemplate(debouncedTemplateContent);
    const currentGenerationId = ++generationIdRef.current;

    generateExampleNote(
      {
        transcript,
        templateStructure: debouncedTemplateContent,
        specialtyName,
      },
      {
        onSuccess: (data) => {
          if (currentGenerationId !== generationIdRef.current) return;
          setLocalExampleContent(data.example_note);
        },
        onError: () => {
          if (currentGenerationId !== generationIdRef.current) return;
          setLocalExampleContent(transcript);
        },
      }
    );
  }, [
    specialtyTranscript?.transcript,
    debouncedTemplateContent,
    specialtyName,
    generateExampleNote,
    persistedExampleForTemplate,
    setPersistedExampleForTemplate,
    setLocalExampleContent,
  ]);

  // Track structure content locally, initialized from props
  const [localStructureContent, setLocalStructureContent] = useState(
    templateContent || DEFAULT_STRUCTURE_CONTENT
  );

  // Update local structure content when templateContent prop changes
  useEffect(() => {
    if (templateContent) {
      setLocalStructureContent(templateContent);
    }
  }, [templateContent]);

  // Cleanup saved timeout on unmount
  useEffect(() => {
    return () => {
      if (savedTimeoutRef.current) {
        clearTimeout(savedTimeoutRef.current);
      }
    };
  }, []);

  // Show saved indicator briefly
  const handleSaved = useCallback(() => {
    setShowSaved(true);
    if (savedTimeoutRef.current) {
      clearTimeout(savedTimeoutRef.current);
    }
    savedTimeoutRef.current = setTimeout(() => {
      setShowSaved(false);
    }, 1500);
  }, []);

  // Handle structure content changes - also propagate to parent for persistence
  const handleStructureChange = useCallback(
    (content: string) => {
      setLocalStructureContent(content);
      onContentChange?.(content);
    },
    [onContentChange]
  );

  // Handle title changes with debounced save
  const debouncedTitleChange = useDebouncedCallback((newName: string) => {
    onTemplateNameChange?.(newName);
    handleSaved();
  }, 300);

  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newName = e.target.value;
      setLocalTemplateName(newName);
      debouncedTitleChange(newName);
    },
    [debouncedTitleChange]
  );

  return {
    // Tab state
    activeTab,
    setActiveTab,
    // Template name
    localTemplateName,
    titleInputRef,
    handleTitleChange,
    // Content state
    localExampleContent,
    setLocalExampleContent,
    localStructureContent,
    handleStructureChange,
    // Saved indicator
    showSaved,
    handleSaved,
    // Example note generation
    isGeneratingExampleNote,
  };
}
